import React, { useState, useEffect } from 'react';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import { organizationAPI } from '../../api/requests/organization/organizationAPI';
import { Badge } from '@mui/material';
import NotificationsPopup from '../../components/NotificationsPopup';
import navbar_logo from '../../assets/Logo/navbar_logo.svg';
import useSafeNavigate from '../../hooks/useSafeNavigate';
import { imageHandler } from '../../utils/imageHandler';
import imageConfig from '../../assets/imageConfig';
import sideBarLogoDark from '../../assets/Logo/logo_dark.svg';
import { height, Stack, width } from '@mui/system';
import { RoundedButton } from '../Form';
import { FiBell, FiMenu } from 'react-icons/fi';
import ShowWhen, { ShowWhenWithAnimation } from '../Others/ShowWhen';
import handleSignOut from '../../utils/handleSignOut';
import scrollToElement from '../../utils/scrollToElement';

import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import SettingsBrightnessOutlinedIcon from '@mui/icons-material/SettingsBrightnessOutlined';
import { useAppTheme } from '../../theme/ThemeProvider/AppTheme';
import adminAPI from '../../api/requests/admin/adminAPI';

const pages = [
  { id: 1, title: 'Home', sectionId: 'hero-section', link: '/#hero-section' },
  { id: 2, title: 'About Us', sectionId: 'demo-section', link: '/#demo-section' },
  { id: 3, title: 'Products', sectionId: 'why-choose-us-section', link: '/#why-choose-us-section' },
  { id: 4, title: 'Pricing', sectionId: 'pricing-section', link: '/#pricing-section' },
  { id: 5, title: 'Careers', link: '/careers' },
  { id: 6, title: 'Contact Us', link: '/contact-support' },
];

export default function Navbar() {

  const navigate = useSafeNavigate();
  const { theme, resolvedTheme, setTheme, toggleTheme, getColor } = useAppTheme()

  const logo = imageHandler.get(imageConfig.navbar.logo) || navbar_logo;

  const [selected, setSelected] = useState(pages.find(page => page.link === location.pathname)?.id || -1);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [themeAnchor, setThemeAnchor] = useState(null);
  const [notificationInfo, setNotificationInfo] = useState({ invites: 0, notifications: 0 });
  const [isNotificationPopupOpen, setIsNotificationPopupOpen] = useState(false);


  const handleNavClick = (info = {}) => {
    setSelected(info.id);

    if (!Object.hasOwn(info, 'sectionId')) {
      navigate(info.link);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    document.title = 'OptiGrit';
    navigate(info.link);

    scrollToElement(`#${info.sectionId}`);
  };


  const fetchNotifications = async () => {
    organizationAPI.getNotifications().then(res => {
      setNotificationInfo(pre => ({ ...pre, notifications: res?.unreadCount ?? 0 }))
    });

    organizationAPI.getInvitesRecievedByUser().then(res => {
      setNotificationInfo(pre => ({ ...pre, invites: res?.length ?? 0 }))
    });
  };


  const isLoggedIn = !!localStorage.getItem('Token');

  useEffect(() => {
    if (isLoggedIn && !isNotificationPopupOpen)
      fetchNotifications();
  }, [isNotificationPopupOpen, isLoggedIn]);


  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      const viewportCenter = window.innerHeight / 2;

      let closestSection = null;
      let smallestDistance = Infinity;

      for (const entry of entries) {
        if (entry.isIntersecting) {
          const rect = entry.target.getBoundingClientRect();
          const sectionCenter = rect.top + rect.height / 2;

          const distance = Math.abs(viewportCenter - sectionCenter);

          if (distance < smallestDistance) {
            smallestDistance = distance;
            closestSection = entry.target.id;
          }
        }
      }

      if (closestSection) {
        const page = pages.find(page => (
          page.sectionId === closestSection
        ));

        if (page) setSelected(page.id);
      }
    }, {
      threshold: 0.1
    });

    const elements = document.querySelectorAll('.section');

    if (elements.length === 0) setSelected(pages.find(page => page.link === location.pathname)?.id || -1);

    elements.forEach(element => observer.observe(element));

    return () => {
      observer.disconnect();
    };

  }, []);

  // Platform-admin entry point. The server answers { isPlatformAdmin } rather
  // than 403ing, so this costs an ordinary user nothing and logs no error.
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  useEffect(() => {
    if (!isLoggedIn) return undefined;
    let cancelled = false;
    adminAPI
      .whoami()
      .then((res) => {
        if (!cancelled) setIsPlatformAdmin(Boolean(res?.isPlatformAdmin));
      })
      .catch(() => undefined); // not an admin, or not reachable — stay hidden
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn]);

  // The bar is transparent while it sits over the hero and only takes on a
  // surface once the page scrolls. Tinting it at rest produced a visible seam
  // against the hero's grid and glow, which begin below the 80px spacer.
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);


  return (
    <Stack>
      <Stack sx={{ width: '100%', height: '80px' }} />
      <Stack sx={{ position: 'fixed', top: '0', left: '0', right: '0', zIndex: '1000', width: '100%', height: '80px', backgroundColor: scrolled ? getColor('bg', 0.82) : 'transparent', backdropFilter: scrolled ? 'blur(12px)' : 'none', borderBottom: '1px solid', borderColor: scrolled ? getColor('border', 0.6) : 'transparent', transition: 'background-color 0.25s ease, border-color 0.25s ease', flexDirection: "row", justifyContent: 'space-between', alignItems: 'center', px: { xs: '16px', md: '24px' }, boxSizing: 'border-box' }} >
        <Stack sx={{ height: '40px' }} >
          <img src={resolvedTheme === 'dark' ? sideBarLogoDark : logo} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: resolvedTheme === 'dark' ? '0 7px' : '0' }} />
        </Stack>

        <Stack sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: "row", alignItems: 'center', gap: '12px', justifyContent: 'center' }} >
          <ShowWhen when={isPlatformAdmin} >
            <Stack
              onClick={() => navigate('/console')}
              sx={{
                cursor: 'pointer', transition: 'all 0.3s',
                px: '10px', py: '3px', borderRadius: '6px',
                border: '1px solid var(--color-primary)',
                color: 'var(--color-primary)',
                '&:hover': { scale: '1.02' }
              }} >
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'inherit' }}>
                Admin
              </Typography>
            </Stack>
          </ShowWhen>

          {pages.map((page) => (
            <Stack
              key={page.id}
              onClick={() => handleNavClick(page)}
              sx={{
                cursor: 'pointer', transition: 'all 0.3s',
                color: selected === page.id ? 'var(--color-text-primary)' : 'var(--color-text)',
                '&:hover': { color: 'var(--color-text-primary)', scale: '1.02' }
              }} >
              <Typography sx={{ fontSize: '14px', color: 'inherit' }}>
                {page.title}
              </Typography>
            </Stack>
          ))}
        </Stack>

        <Stack sx={{ flexDirection: "row", alignItems: 'center', gap: '20px' }} >
          <IconButton sx={{ color: 'var(--color-text)' }} onClick={(e) => setThemeAnchor(e.currentTarget)} >
            <ShowWhenWithAnimation
              containerStyle={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px' }}
              style={{ alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px' }}
              display='flex'
              when={theme === 'light'}
              otherwise={
                theme === 'system' ? <SettingsBrightnessOutlinedIcon style={{ fontSize: '20px' }} /> : <DarkModeOutlinedIcon style={{ fontSize: '20px' }} />
              }
            >
              <LightModeOutlinedIcon style={{ fontSize: '20px' }} />
            </ShowWhenWithAnimation>
          </IconButton>

          <Menu
            anchorEl={themeAnchor}
            open={Boolean(themeAnchor)}
            onClose={() => setThemeAnchor(null)}
          >
            {[
              { value: 'light', label: 'Light', icon: LightModeOutlinedIcon },
              { value: 'dark', label: 'Dark', icon: DarkModeOutlinedIcon },
              { value: 'system', label: 'System', icon: SettingsBrightnessOutlinedIcon }
            ].map((option) => (
              <MenuItem key={option.value} onClick={() => { setTheme(option.value); setThemeAnchor(null); }} selected={theme === option.value} sx={{ '&.Mui-selected': { backgroundColor: 'var(--color-primary)', color: 'white', '&:hover': { backgroundColor: 'var(--color-primary)' } } }}>
                <ListItemIcon>
                  <option.icon fontSize="small" sx={{ color: theme === option.value ? 'white' : 'inherit' }} />
                </ListItemIcon>
                <Typography sx={{ fontSize: '14px', color: theme === option.value && 'white' }}>{option.label}</Typography>
              </MenuItem>
            ))}
          </Menu>

          <ShowWhen when={isLoggedIn} >
            <IconButton sx={{ color: 'var(--color-text)' }} onClick={() => setIsNotificationPopupOpen(!isNotificationPopupOpen)} >
              <Badge badgeContent={notificationInfo.invites + notificationInfo.notifications} color="primary" max={99} >
                <FiBell style={{ fontSize: '20px' }} />
              </Badge>
            </IconButton>

            <NotificationsPopup
              open={isNotificationPopupOpen}
              handleClose={() => setIsNotificationPopupOpen(false)}
            />
          </ShowWhen>

          <RoundedButton
            sx={{ height: '36px', display: { xs: 'none', md: 'flex' } }}
            onClick={() => {
              if (isLoggedIn) {
                handleSignOut()
              }
              navigate('/sign-in');
            }}
          >
            {isLoggedIn ? 'Logout' : 'Sign In'}
          </RoundedButton>

          <Stack sx={{ display: { xs: 'flex', md: 'none' } }} >
            <IconButton size='large' sx={{ color: 'var(--color-text)' }} onClick={(e) => setMenuAnchor(e.currentTarget)} >
              <FiMenu />
            </IconButton>

            <Menu
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
              anchorEl={menuAnchor}
            >
              {pages.map((page) => (
                <MenuItem
                  key={page.id}
                  onClick={() => handleNavClick(page)}
                  sx={{
                    cursor: 'pointer', minWidth: '180px', color: selected === page.id ? 'var(--color-primary)' : 'var(--color-text)', transition: 'all 0.3s',
                    '&:hover': { color: 'var(--color-primary)', scale: '1.02' }
                  }}
                >
                  <Typography sx={{ fontSize: '14px' }} >{page.title}</Typography>
                </MenuItem>
              ))}

              <MenuItem>
                <RoundedButton
                  fullWidth
                  sx={{ height: '36px' }}
                  onClick={() => navigate(isLoggedIn ? '/organization' : '/sign-in')}
                >
                  {isLoggedIn ? 'Dashboard' : 'Sign In'}
                </RoundedButton>
              </MenuItem>

              <ShowWhen when={isLoggedIn} >
                <MenuItem sx={{ display: { xs: 'flex', md: 'none' } }} >
                  <RoundedButton
                    fullWidth
                    variant='outlined'
                    sx={{ height: '36px' }}
                    onClick={() => handleSignOut()}
                  >
                    Logout
                  </RoundedButton>
                </MenuItem>
              </ShowWhen>
            </Menu>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  )
}
